import { useCallback, useState } from "react";
import {
  CreateOneProductInput,
  EnumBookerStatus,
  EnumProductPublicationStatus,
  EnumProductType,
  MembershipCheckoutBookersDocument,
  OrganizationByIdQuery,
  useBookerCreateMutation,
  useBookerUpdateMutation,
  useProductCreateMutation,
  useStorefrontOwnerEntitiesQuery,
} from "gql/generated";
import { createMultilingual } from "shared/utils";
import { usePostHog } from "shared/hooks";
import { buildSellingRecord } from "apps/storefront/setupBookingWidget/flyouts/AddProductFlyout/shared/SellingSection/sellingConfigForm";

type RenewalSettings = NonNullable<
  NonNullable<
    NonNullable<OrganizationByIdQuery["organizationById"]>["metadata"]
  >["renewalSettings"]
>;

/** Mirrors backend `resolveEffectiveRecurringConfig` so the saved product matches what the server will inherit. */
const getRecurringFromRenewalSettings = (settings?: RenewalSettings | null) => {
  const isFixedDate = settings?.renewalType === "FIXED_DATE";
  const billingIntervalMonths =
    settings?.term === "MONTHLY"
      ? 1
      : settings?.term === "MULTI_YEAR"
        ? (settings.termYears ?? 1) * 12
        : 12;
  return {
    recurringBillingIntervalMonths: billingIntervalMonths,
    recurringRenewalType: isFixedDate ? "FIXED_DATE" : "PURCHASE_DATE",
    recurringFixedRenewalMonth: isFixedDate
      ? (settings?.fixedRenewalMonth ?? undefined)
      : undefined,
    recurringFixedRenewalDay: isFixedDate
      ? (settings?.fixedRenewalDay ?? undefined)
      : undefined,
    recurringProRateEnabled: isFixedDate
      ? (settings?.proRateEnabled ?? false)
      : false,
    recurringMembershipLinked: true,
  };
};

type UseCreateMembershipCheckoutArgs = {
  targetStorefrontId?: string;
  connectedOperatorId?: string;
  renewalSettings?: RenewalSettings | null;
  onAssociate: (tierTagId: string, bookerId: string) => Promise<void>;
};

export const useCreateMembershipCheckout = ({
  targetStorefrontId,
  connectedOperatorId,
  renewalSettings,
  onAssociate,
}: UseCreateMembershipCheckoutArgs) => {
  const { trackPostHogException } = usePostHog();
  const [creating, setCreating] = useState(false);

  const { data: ownerData } = useStorefrontOwnerEntitiesQuery({
    variables: { storefrontId: targetStorefrontId ?? "" },
    skip: !targetStorefrontId,
  });
  const ownerEntities = ownerData?.storefrontOwnerEntities ?? [];
  const owner =
    ownerEntities.find(entity => entity.entityId === connectedOperatorId) ??
    ownerEntities[0];

  const [createBooker] = useBookerCreateMutation();
  const [createProduct] = useProductCreateMutation();
  const [updateBooker] = useBookerUpdateMutation({
    refetchQueries: [MembershipCheckoutBookersDocument],
    awaitRefetchQueries: true,
  });

  const createCheckout = useCallback(
    async ({
      tierTagId,
      name,
      price,
    }: {
      tierTagId: string;
      name: string;
      price: number;
    }): Promise<{ bookerId: string; storefrontId: string }> => {
      if (!targetStorefrontId) throw new Error("No storefront connected");
      if (!owner) throw new Error("No listing is connected to the storefront");

      setCreating(true);
      try {
        const bookerResult = await createBooker({
          variables: {
            record: {
              storefront: targetStorefrontId,
              name: createMultilingual(name),
              bundles: [],
              discounts: [],
              policies: [],
              rules: [],
              waivers: [],
              status: EnumBookerStatus.Draft,
              businessCompletionTags: [tierTagId],
              customerFields: {
                businessFieldEnabled: true,
                phoneEnabled: true,
                guestNamesEnabled: false,
                guestEmailEnabled: false,
                guestInfoRequired: false,
                addressRequired: false,
              },
            },
          },
        });
        const bookerId = bookerResult.data?.bookerCreate?.record?._id;
        if (!bookerId) throw new Error("No booker id returned");

        const productResult = await createProduct({
          variables: {
            record: {
              name: createMultilingual(name),
              description: createMultilingual(""),
              shortDescription: createMultilingual(""),
              ...buildSellingRecord({
                values: { price, sellingOptions: [] },
                product: null,
                ...getRecurringFromRenewalSettings(renewalSettings),
              }),
              owner: { type: owner.type, entityId: owner.entityId },
              isActive: true,
              isUpsellOnly: false,
              storefront: targetStorefrontId,
              type: EnumProductType.RenewableFee,
              publicationStatus: EnumProductPublicationStatus.Approved,
            } as CreateOneProductInput,
            inventoryCount: 1,
          },
        });
        const productId = productResult.data?.productCreate?.record?._id;
        if (!productId) throw new Error("No product id returned");

        await updateBooker({
          variables: {
            id: bookerId,
            record: { bundles: [{ product: productId, upsells: [] }] },
          },
        });

        await onAssociate(tierTagId, String(bookerId));
        return { bookerId: String(bookerId), storefrontId: targetStorefrontId };
      } catch (error) {
        trackPostHogException(error, {
          fileName: "useCreateMembershipCheckout.ts",
          method: "createCheckout",
          message: "Error creating membership checkout widget",
        });
        throw error;
      } finally {
        setCreating(false);
      }
    },
    [
      targetStorefrontId,
      owner,
      renewalSettings,
      createBooker,
      createProduct,
      updateBooker,
      onAssociate,
      trackPostHogException,
    ],
  );

  return { createCheckout, creating, hasOwner: !!owner };
};
